import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import { Plus, Printer, Check, Pencil, Package, QrCode, Grid3x3 } from "lucide-react";

import { fetchMasterRaking, createMasterRaking, updateMasterRaking } from "./masterRakingSlice";
import { fetchMasterProducts } from "./masterProductsSlice";
import { fetchMasterWarehouses } from "./warehouseSlice";
import { listRakApi } from "../../auth/authService";
import PrintLabels from "./PrintLabels";
import PrintLevelLabels from "./PrintLevelLabels";
import {
    PageHeader,
    Button,
    SearchInput,
    Segmented,
    Field,
    inputClass,
    Modal,
    Pager,
    errorMessage
} from "../../components/MasterUi";

const PER_PAGE = 12;

const EMPTY_FORM = {
    code: "",
    bins_name: "",
    total_rows: "",
    total_columns: "",
    total_capacity: "",
    warehouse_id: "",
    product_ids: []
};

const LABEL_PAGE_STYLE = `
    @page { size: 101mm 53mm; margin: 0; }
    body { margin: 0; }
`;

/* checkbox kategori: tercentang / sebagian / kosong */
function TriCheckbox({ checked, indeterminate, onChange, label }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            aria-label={label}
            className="w-4 h-4 rounded border-line text-ink focus:ring-ink"
        />
    );
}

export default function MasterRakingPage() {
    const dispatch = useDispatch();
    const printRef = useRef();
    const levelPrintRef = useRef();

    const masterRaking = useSelector((state) => state.masterRaking);
    const masterProducts = useSelector((state) => state.masterProducts);
    const masterWarehouses = useSelector((state) => state.masterWarehouses);
    const token = useSelector((state) => state.auth?.token);

    const data = masterRaking?.data || [];
    const pagination = masterRaking?.pagination || {};
    const loading = masterRaking?.loading || false;
    const products = masterProducts?.data || [];
    const warehouses = masterWarehouses?.data || [];

    const [searchInput, setSearchInput] = useState("");
    const [filters, setFilters] = useState({ search: "", page: 1, per_page: PER_PAGE });

    const [printMode, setPrintMode] = useState("multi");
    const [selected, setSelected] = useState({});
    const [printData, setPrintData] = useState([]);
    const [printing, setPrinting] = useState(false);

    // form tambah / ubah
    const [openForm, setOpenForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [capacityTouched, setCapacityTouched] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [productSearch, setProductSearch] = useState("");
    const [onlySelected, setOnlySelected] = useState(false);

    // detail produk & QR slot
    const [detailRack, setDetailRack] = useState(null);
    const [levelRack, setLevelRack] = useState(null);
    const [levelData, setLevelData] = useState([]);

    /* ===== ambil data ===== */

    useEffect(() => {
        dispatch(fetchMasterRaking(filters));
    }, [dispatch, filters]);

    // master produk & warehouse cukup diambil sekali
    useEffect(() => {
        dispatch(fetchMasterProducts({ page: 1, per_page: 1000 }));
        dispatch(fetchMasterWarehouses());
    }, [dispatch]);

    useEffect(() => {
        const t = setTimeout(() => {
            setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }));
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    /* ===== cetak QR rak ===== */

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: "QR Rak",
        pageStyle: LABEL_PAGE_STYLE,
        onAfterPrint: () => setPrinting(false)
    });

    const handlePrintLevel = useReactToPrint({
        contentRef: levelPrintRef,
        documentTitle: "QR Slot Rak",
        pageStyle: LABEL_PAGE_STYLE
    });

    const selectedCount = Object.keys(selected).length;
    const totalAll = pagination?.total || 0;

    const handlePrintClick = async () => {
        try {
            setPrinting(true);
            let items;
            if (printMode === "multi") {
                if (
                    totalAll > 500 &&
                    !window.confirm(`Cetak ${totalAll.toLocaleString("id-ID")} label QR rak sekaligus? Proses ini bisa memakan waktu.`)
                ) {
                    setPrinting(false);
                    return;
                }
                const res = await listRakApi(token, {
                    page: 1,
                    per_page: Math.max(totalAll, 1),
                    search: filters.search
                });
                items = res?.data || [];
            } else {
                items = Object.values(selected);
            }

            if (!items.length) {
                toast.info(printMode === "multi" ? "Tidak ada rak untuk dicetak." : "Pilih rak yang akan dicetak dulu.");
                setPrinting(false);
                return;
            }
            setPrintData(items);
            setTimeout(() => handlePrint(), 300);
        } catch {
            setPrinting(false);
            toast.error("Data rak untuk dicetak tidak bisa diambil. Coba lagi.");
        }
    };

    const toggleSelect = (item) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[item.id]) delete next[item.id];
            else next[item.id] = item;
            return next;
        });
    };

    const allOnPageSelected = data.length > 0 && data.every((d) => selected[d.id]);
    const togglePage = () => {
        setSelected((prev) => {
            const next = { ...prev };
            if (allOnPageSelected) data.forEach((d) => delete next[d.id]);
            else data.forEach((d) => (next[d.id] = d));
            return next;
        });
    };

    /* ===== QR per slot ===== */

    const openLevelQR = (rack) => {
        const rows = Number(rack.total_rows) || 0;
        const cols = Number(rack.total_columns) || 0;
        const result = [];
        for (let r = 1; r <= rows; r++) {
            for (let c = 1; c <= cols; c++) {
                result.push({
                    id: `${rack.id}-${r}-${c}`,
                    code: `${rack.racks_name} ${r}.${c}`,
                    rack_name: rack.racks_name,
                    warehouse: rack.warehouse?.name
                });
            }
        }
        setLevelData(result);
        setLevelRack(rack);
    };

    /* ===== form ===== */

    const openCreate = () => {
        setEditId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setCapacityTouched(false);
        setProductSearch("");
        setOnlySelected(false);
        setOpenForm(true);
    };

    const openEdit = (item) => {
        setEditId(item.id);
        setForm({
            code: item.code || "",
            bins_name: item.racks_name || "",
            total_rows: item.total_rows ?? "",
            total_columns: item.total_columns ?? "",
            total_capacity: item.total_capacity ?? "",
            warehouse_id: item.warehouse_id ? String(item.warehouse_id) : "",
            product_ids: item.products?.map((p) => p.id) || []
        });
        setErrors({});
        // anggap kapasitas sudah diatur manual kalau berbeda dari baris × kolom
        setCapacityTouched(Number(item.total_capacity) !== Number(item.total_rows) * Number(item.total_columns));
        setProductSearch("");
        setOnlySelected(false);
        setOpenForm(true);
    };

    const setDim = (key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            if (!capacityTouched) {
                const r = Number(next.total_rows);
                const c = Number(next.total_columns);
                next.total_capacity = r > 0 && c > 0 ? String(r * c) : "";
            }
            return next;
        });
    };

    const slots = (Number(form.total_rows) || 0) * (Number(form.total_columns) || 0);

    const validate = () => {
        const e = {};
        if (!String(form.code).trim()) e.code = "Isi kode rak.";
        if (!String(form.bins_name).trim()) e.bins_name = "Isi nama rak.";
        if (!form.warehouse_id) e.warehouse_id = "Pilih warehouse.";
        if (!(Number(form.total_rows) >= 1)) e.total_rows = "Minimal 1.";
        if (!(Number(form.total_columns) >= 1)) e.total_columns = "Minimal 1.";
        if (!(Number(form.total_capacity) >= 1)) e.total_capacity = "Minimal 1.";
        if (!form.product_ids.length) e.product_ids = "Pilih minimal 1 produk yang boleh disimpan di rak ini.";
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;

        const payload = {
            ...form,
            code: String(form.code).trim(),
            bins_name: String(form.bins_name).trim(),
            total_rows: Number(form.total_rows),
            total_columns: Number(form.total_columns),
            total_capacity: Number(form.total_capacity)
        };

        try {
            setSaving(true);
            const res = editId
                ? await dispatch(updateMasterRaking({ id: editId, ...payload })).unwrap()
                : await dispatch(createMasterRaking(payload)).unwrap();

            const msg = res?.message && res.message !== "success" ? res.message : null;
            toast.success(msg || (editId ? `Rak ${payload.code} diperbarui.` : `Rak ${payload.code} ditambahkan.`));
            setOpenForm(false);
            dispatch(fetchMasterRaking(filters));
        } catch (err) {
            const m = err?.message;
            if (m && typeof m === "object") {
                const pick = (k) => (m[k] ? [].concat(m[k]).join(" ") : undefined);
                setErrors({ code: pick("code"), product_ids: pick("product_ids") });
            }
            toast.error(errorMessage(err, "Rak gagal disimpan."));
        } finally {
            setSaving(false);
        }
    };

    /* ===== produk dikelompokkan per kategori ===== */

    const groupedProducts = useMemo(() => {
        const q = productSearch.trim().toLowerCase();
        const sel = new Set(form.product_ids);
        const groups = {};
        products.forEach((p) => {
            if (onlySelected && !sel.has(p.id)) return;
            if (q && !(p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q))) return;
            const cat = p.categories?.name || "Tanpa kategori";
            (groups[cat] = groups[cat] || []).push(p);
        });
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [products, productSearch, onlySelected, form.product_ids]);

    const toggleProduct = (id) => {
        setForm((prev) => ({
            ...prev,
            product_ids: prev.product_ids.includes(id)
                ? prev.product_ids.filter((x) => x !== id)
                : [...prev.product_ids, id]
        }));
    };

    const toggleGroup = (items, allChecked) => {
        const ids = items.map((p) => p.id);
        setForm((prev) => ({
            ...prev,
            product_ids: allChecked
                ? prev.product_ids.filter((id) => !ids.includes(id))
                : [...new Set([...prev.product_ids, ...ids])]
        }));
    };

    const printLabel = printMode === "multi"
        ? `Cetak QR (${totalAll.toLocaleString("id-ID")})`
        : `Cetak QR (${selectedCount})`;

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            <PageHeader title="Master rak" subtitle="Daftar rak, ukuran slot, produk yang boleh disimpan, dan label QR">
                <Button variant="primary" onClick={openCreate}>
                    <Plus size={16} /> Tambah rak
                </Button>
            </PageHeader>

            {/* ===== toolbar ===== */}
            <section className="bg-white rounded-lg border border-line p-4 flex flex-col lg:flex-row lg:items-center gap-3 no-print print:hidden">
                <SearchInput
                    value={searchInput}
                    onChange={setSearchInput}
                    placeholder="Cari kode rak"
                    className="lg:w-80"
                />

                <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
                    <Segmented
                        label="Mode cetak"
                        value={printMode}
                        onChange={setPrintMode}
                        options={[
                            { value: "multi", label: filters.search ? "Semua hasil pencarian" : "Semua rak" },
                            { value: "single", label: "Pilih manual" }
                        ]}
                    />
                    <Button
                        variant="primary"
                        onClick={handlePrintClick}
                        disabled={printing || (printMode === "single" && selectedCount === 0)}
                    >
                        <Printer size={16} /> {printing ? "Menyiapkan…" : printLabel}
                    </Button>
                </div>
            </section>

            {printMode === "single" && (
                <div className="flex flex-wrap items-center gap-3 text-sm -mt-2">
                    <span className="text-muted">
                        <span className="font-semibold text-body">{selectedCount}</span> rak dipilih, tetap tersimpan walau pindah halaman.
                    </span>
                    <button type="button" onClick={togglePage} className="text-ink font-medium hover:underline">
                        {allOnPageSelected ? "Batalkan pilihan di halaman ini" : "Pilih semua di halaman ini"}
                    </button>
                    {selectedCount > 0 && (
                        <button type="button" onClick={() => setSelected({})} className="text-muted hover:text-body hover:underline">
                            Hapus semua pilihan
                        </button>
                    )}
                </div>
            )}

            {/* ===== kartu rak ===== */}
            <section className={`transition-opacity ${loading ? "opacity-60" : ""}`}>
                {data.length === 0 && !loading ? (
                    <div className="bg-white rounded-lg border border-dashed border-line px-6 py-14 text-center">
                        <div className="font-semibold text-body">
                            {filters.search ? "Rak tidak ditemukan" : "Belum ada rak"}
                        </div>
                        <p className="text-sm text-muted mt-1">
                            {filters.search
                                ? `Tidak ada rak dengan kode "${filters.search}".`
                                : "Tambahkan rak pertama beserta ukuran slot dan produknya."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                        {data.map((item) => {
                            const isSelected = Boolean(selected[item.id]);
                            const selectable = printMode === "single";
                            const slotCount = (Number(item.total_rows) || 0) * (Number(item.total_columns) || 0);
                            const productCount = item.products?.length || 0;

                            return (
                                <article
                                    key={item.id}
                                    className={`relative bg-white rounded-lg border flex flex-col transition-colors ${
                                        isSelected ? "border-ink ring-2 ring-ink/20" : "border-line"
                                    }`}
                                >
                                    <div
                                        className={`flex items-start gap-4 p-4 ${selectable ? "cursor-pointer" : ""}`}
                                        onClick={selectable ? () => toggleSelect(item) : undefined}
                                        role={selectable ? "button" : undefined}
                                        aria-pressed={selectable ? isSelected : undefined}
                                        tabIndex={selectable ? 0 : undefined}
                                        onKeyDown={selectable ? (e) => (e.key === " " || e.key === "Enter") && (e.preventDefault(), toggleSelect(item)) : undefined}
                                    >
                                        <div className="shrink-0 bg-white p-1 rounded border border-line">
                                            <QRCodeCanvas value={item.code} size={88} />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="text-lg font-bold text-body tabular-nums leading-tight">{item.code}</div>
                                            <div className="text-sm text-body truncate">{item.racks_name}</div>
                                            <div className="text-xs text-muted truncate">{item.warehouse?.name || "Tanpa warehouse"}</div>

                                            <div className="flex flex-wrap gap-1.5 mt-3">
                                                <span className="inline-flex items-center gap-1 text-xs bg-surface border border-line rounded px-1.5 py-0.5 tabular-nums">
                                                    <Grid3x3 size={12} className="text-muted" />
                                                    {item.total_rows} × {item.total_columns} = {slotCount} slot
                                                </span>
                                                <span className="inline-flex items-center gap-1 text-xs bg-surface border border-line rounded px-1.5 py-0.5 tabular-nums">
                                                    <Package size={12} className="text-muted" />
                                                    {productCount} produk
                                                </span>
                                                {Number(item.total_capacity) !== slotCount && (
                                                    <span
                                                        className="text-xs bg-tag-soft text-amber-800 rounded px-1.5 py-0.5 tabular-nums"
                                                        title="Kapasitas berbeda dengan jumlah slot (baris × kolom)"
                                                    >
                                                        kapasitas {item.total_capacity}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {selectable && (
                                            <span
                                                className={`absolute top-3 right-3 w-5 h-5 rounded border flex items-center justify-center ${
                                                    isSelected ? "bg-ink border-ink text-white" : "bg-white border-line"
                                                }`}
                                                aria-hidden="true"
                                            >
                                                {isSelected && <Check size={14} strokeWidth={3} />}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-auto grid grid-cols-3 border-t border-line text-sm">
                                        <button
                                            type="button"
                                            onClick={() => setDetailRack(item)}
                                            className="flex items-center justify-center gap-1.5 h-10 text-body hover:bg-surface"
                                        >
                                            <Package size={15} className="text-muted" /> Produk
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openLevelQR(item)}
                                            className="flex items-center justify-center gap-1.5 h-10 text-body hover:bg-surface border-x border-line"
                                        >
                                            <QrCode size={15} className="text-muted" /> QR slot
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openEdit(item)}
                                            className="flex items-center justify-center gap-1.5 h-10 text-ink font-medium hover:bg-ink-100"
                                        >
                                            <Pencil size={15} /> Ubah
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>

            {totalAll > 0 && (
                <Pager pagination={pagination} unit="rak" onChange={(page) => setFilters((prev) => ({ ...prev, page }))} />
            )}

            {/* area cetak tersembunyi */}
            <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
                <PrintLabels ref={printRef} data={printData} />
                <PrintLevelLabels ref={levelPrintRef} data={levelData} />
            </div>

            {/* ===== modal tambah / ubah ===== */}
            <Modal
                open={openForm}
                onClose={() => !saving && setOpenForm(false)}
                title={editId ? `Ubah rak ${form.code}` : "Tambah rak"}
                subtitle={editId ? "Perubahan ukuran tidak memindahkan stok yang sudah ada di rak." : "Isi identitas, ukuran slot, dan produk yang boleh disimpan."}
                size="lg"
                footer={
                    <>
                        <Button onClick={() => setOpenForm(false)} disabled={saving}>Batal</Button>
                        <Button variant="primary" type="submit" form="form-rak" disabled={saving}>
                            {saving ? "Menyimpan…" : editId ? "Simpan perubahan" : "Simpan rak"}
                        </Button>
                    </>
                }
            >
                <form id="form-rak" onSubmit={handleSubmit} className="space-y-6">

                    {/* identitas */}
                    <fieldset className="space-y-4">
                        <legend className="text-sm font-semibold text-body mb-3">Identitas rak</legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Kode rak" hint="Dipakai sebagai isi QR rak, mis. R014." error={errors.code}>
                                <input
                                    type="text"
                                    autoFocus
                                    value={form.code}
                                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    className={inputClass(errors.code)}
                                />
                            </Field>
                            <Field label="Nama rak" hint="Dipakai di label slot, mis. RAK A." error={errors.bins_name}>
                                <input
                                    type="text"
                                    value={form.bins_name}
                                    onChange={(e) => setForm({ ...form, bins_name: e.target.value })}
                                    className={inputClass(errors.bins_name)}
                                />
                            </Field>
                        </div>
                        <Field label="Warehouse" error={errors.warehouse_id}>
                            <select
                                value={form.warehouse_id}
                                onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                                className={inputClass(errors.warehouse_id)}
                            >
                                <option value="">Pilih warehouse</option>
                                {warehouses.map((w) => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        </Field>
                    </fieldset>

                    {/* ukuran */}
                    <fieldset>
                        <legend className="text-sm font-semibold text-body mb-3">Ukuran slot</legend>
                        <div className="grid grid-cols-3 gap-4">
                            <Field label="Baris (level)" error={errors.total_rows}>
                                <input
                                    type="number"
                                    min="1"
                                    inputMode="numeric"
                                    value={form.total_rows}
                                    onChange={(e) => setDim("total_rows", e.target.value)}
                                    className={inputClass(errors.total_rows)}
                                />
                            </Field>
                            <Field label="Kolom" error={errors.total_columns}>
                                <input
                                    type="number"
                                    min="1"
                                    inputMode="numeric"
                                    value={form.total_columns}
                                    onChange={(e) => setDim("total_columns", e.target.value)}
                                    className={inputClass(errors.total_columns)}
                                />
                            </Field>
                            <Field
                                label="Kapasitas"
                                error={errors.total_capacity}
                                hint={capacityTouched ? "Diatur manual." : "Otomatis baris × kolom."}
                            >
                                <input
                                    type="number"
                                    min="1"
                                    inputMode="numeric"
                                    value={form.total_capacity}
                                    onChange={(e) => {
                                        setCapacityTouched(true);
                                        setForm({ ...form, total_capacity: e.target.value });
                                    }}
                                    className={inputClass(errors.total_capacity)}
                                />
                            </Field>
                        </div>

                        {slots > 0 && (
                            <div className="mt-3 rounded-lg border border-line bg-surface/60 p-3">
                                <div className="text-xs text-muted mb-2">
                                    Pratinjau: {form.total_rows} baris × {form.total_columns} kolom = <span className="font-semibold text-body">{slots} slot</span>
                                    {capacityTouched && Number(form.total_capacity) !== slots && (
                                        <span className="text-amber-700">, kapasitas diisi {form.total_capacity}</span>
                                    )}
                                </div>
                                {slots <= 120 ? (
                                    <div
                                        className="grid gap-1"
                                        style={{ gridTemplateColumns: `repeat(${Number(form.total_columns)}, minmax(0, 1fr))`, maxWidth: Number(form.total_columns) * 40 }}
                                    >
                                        {Array.from({ length: slots }).map((_, i) => (
                                            <div
                                                key={i}
                                                className="h-6 rounded-sm bg-white border border-line text-[9px] text-muted flex items-center justify-center tabular-nums"
                                            >
                                                {Math.floor(i / Number(form.total_columns)) + 1}.{(i % Number(form.total_columns)) + 1}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-xs text-muted">Terlalu banyak slot untuk dipratinjau.</div>
                                )}
                            </div>
                        )}
                    </fieldset>

                    {/* produk */}
                    <fieldset>
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                            <legend className="text-sm font-semibold text-body">Produk yang boleh disimpan</legend>
                            <span className="text-xs text-muted">
                                <span className="font-semibold text-body">{form.product_ids.length}</span> dari {products.length} produk dipilih
                            </span>
                        </div>

                        <div className={`rounded-lg border ${errors.product_ids ? "border-red-400" : "border-line"}`}>
                            <div className="flex flex-col sm:flex-row gap-2 p-3 border-b border-line">
                                <SearchInput
                                    value={productSearch}
                                    onChange={setProductSearch}
                                    placeholder="Cari SKU atau nama produk"
                                    className="flex-1"
                                />
                                <label className="flex items-center gap-2 text-sm text-body whitespace-nowrap px-1">
                                    <input
                                        type="checkbox"
                                        checked={onlySelected}
                                        onChange={(e) => setOnlySelected(e.target.checked)}
                                        className="w-4 h-4 rounded border-line text-ink focus:ring-ink"
                                    />
                                    Hanya yang dipilih
                                </label>
                            </div>

                            <div className="max-h-72 overflow-y-auto divide-y divide-line">
                                {groupedProducts.length === 0 ? (
                                    <div className="text-center text-sm text-muted py-8">
                                        {onlySelected ? "Belum ada produk yang dipilih." : "Produk tidak ditemukan."}
                                    </div>
                                ) : (
                                    groupedProducts.map(([category, items]) => {
                                        const selCount = items.filter((p) => form.product_ids.includes(p.id)).length;
                                        const allChecked = selCount === items.length;
                                        return (
                                            <div key={category}>
                                                <label className="sticky top-0 z-10 flex items-center gap-3 px-3 py-2 bg-surface text-sm font-semibold text-body cursor-pointer">
                                                    <TriCheckbox
                                                        checked={allChecked}
                                                        indeterminate={selCount > 0 && !allChecked}
                                                        onChange={() => toggleGroup(items, allChecked)}
                                                        label={`Pilih semua ${category}`}
                                                    />
                                                    <span className="flex-1">{category}</span>
                                                    <span className="text-xs font-normal text-muted tabular-nums">
                                                        {selCount}/{items.length}
                                                    </span>
                                                </label>
                                                <ul>
                                                    {items.map((p) => (
                                                        <li key={p.id}>
                                                            <label className="flex items-center gap-3 px-3 py-2 pl-10 text-sm hover:bg-surface/60 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={form.product_ids.includes(p.id)}
                                                                    onChange={() => toggleProduct(p.id)}
                                                                    className="w-4 h-4 rounded border-line text-ink focus:ring-ink"
                                                                />
                                                                <span className="tabular-nums text-muted w-24 shrink-0">{p.sku}</span>
                                                                <span className="text-body truncate">{p.name}</span>
                                                            </label>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        {errors.product_ids && <div className="text-xs text-red-600 mt-1">{errors.product_ids}</div>}
                    </fieldset>
                </form>
            </Modal>

            {/* ===== modal produk di rak ===== */}
            <Modal
                open={Boolean(detailRack)}
                onClose={() => setDetailRack(null)}
                title={`Produk di rak ${detailRack?.code || ""}`}
                subtitle={`${detailRack?.racks_name || ""}, ${detailRack?.products?.length || 0} produk`}
                size="md"
                footer={
                    <>
                        <Button onClick={() => setDetailRack(null)}>Tutup</Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                const r = detailRack;
                                setDetailRack(null);
                                openEdit(r);
                            }}
                        >
                            <Pencil size={15} /> Ubah produk
                        </Button>
                    </>
                }
            >
                {detailRack?.products?.length ? (
                    <ul className="divide-y divide-line -my-2">
                        {detailRack.products.map((p) => (
                            <li key={p.id} className="py-2.5 flex gap-3 text-sm">
                                <span className="tabular-nums text-muted w-24 shrink-0">{p.sku}</span>
                                <span className="text-body">{p.name}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-sm text-muted text-center py-6">Belum ada produk yang diatur untuk rak ini.</div>
                )}
            </Modal>

            {/* ===== modal QR slot ===== */}
            <Modal
                open={Boolean(levelRack)}
                onClose={() => setLevelRack(null)}
                title={`QR slot ${levelRack?.racks_name || ""}`}
                subtitle={`${levelData.length} label, satu per slot (baris.kolom)`}
                size="xl"
                footer={
                    <>
                        <Button onClick={() => setLevelRack(null)}>Tutup</Button>
                        <Button variant="primary" onClick={handlePrintLevel} disabled={!levelData.length}>
                            <Printer size={16} /> Cetak {levelData.length} label
                        </Button>
                    </>
                }
            >
                {levelData.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {levelData.map((item) => (
                            <div key={item.id} className="flex items-center gap-3 rounded-lg border border-line p-2.5">
                                <QRCodeCanvas value={item.code} size={64} />
                                <div className="text-sm font-bold text-body leading-tight">{item.code}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-muted text-center py-6">
                        Ukuran rak belum diatur. Isi jumlah baris dan kolom lewat tombol Ubah.
                    </div>
                )}
            </Modal>
        </div>
    );
}
